/**
 * Add documents from an Azure Blob connection: pick a connection, then either
 * several files or a whole folder (recursive, server-side).
 *
 * Reuses the existing `BlobFilePicker` (container → folder → file) rather than
 * a second picker: its multi-select mode covers "these files", its folder mode
 * covers "everything under here".
 */

import { useState } from "react";
import { Button, Group, SegmentedControl, Select, Stack, Text } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { BlobFilePicker, type BlobSelection } from "@/features/ai/BlobFilePicker";
import { addDocuments, type AddDocumentsOut } from "@/shared/api/indexes";
import { toastApiError } from "@/shared/ui/toast";
import { UPLOAD_EXTENSIONS } from "./collectionMeta";
import { useConnectionsOf } from "./useConnectionsOf";

interface Props {
  indexId: string;
  onAdded: (result: AddDocumentsOut) => void;
}

export function BlobSourceTab({ indexId, onAdded }: Props) {
  const getToken = useAccessToken();
  const connections = useConnectionsOf("blob");
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [mode, setMode] = useState<"files" | "folder">("files");
  const [files, setFiles] = useState<BlobSelection[]>([]);
  const [folder, setFolder] = useState<BlobSelection | null>(null);
  const [busy, setBusy] = useState(false);

  const toggle = (sel: BlobSelection) =>
    setFiles((prev) =>
      prev.some((p) => p.container === sel.container && p.path === sel.path)
        ? prev.filter((p) => !(p.container === sel.container && p.path === sel.path))
        : [...prev, sel],
    );

  const reset = () => {
    setFiles([]);
    setFolder(null);
  };

  // Files may span containers only if the user switched container mid-way; the
  // API takes one container per call, so group by container.
  const submit = async () => {
    if (!connectionId) return;
    setBusy(true);
    try {
      const token = await getToken();
      if (mode === "folder") {
        if (!folder) return;
        onAdded(
          await addDocuments(token, indexId, {
            source_kind: "blob",
            connection_id: connectionId,
            container: folder.container,
            folder: folder.path,
          }),
        );
      } else {
        const byContainer = new Map<string, string[]>();
        for (const f of files) {
          byContainer.set(f.container, [...(byContainer.get(f.container) ?? []), f.path]);
        }
        for (const [container, paths] of byContainer) {
          onAdded(
            await addDocuments(token, indexId, {
              source_kind: "blob",
              connection_id: connectionId,
              container,
              paths,
            }),
          );
        }
      }
      reset();
    } catch (err) {
      toastApiError("Could not add Blob documents", err);
    } finally {
      setBusy(false);
    }
  };

  const canSubmit = connectionId && (mode === "folder" ? folder !== null : files.length > 0);

  return (
    <Stack gap="sm">
      <Select
        label="Blob connection"
        placeholder={connections === null ? "Loading…" : "Choose a connection"}
        data={(connections ?? []).map((c) => ({ value: c.connection_id, label: c.display_name }))}
        value={connectionId}
        onChange={(v) => {
          setConnectionId(v);
          reset();
        }}
        disabled={connections === null}
        nothingFoundMessage="No Blob connections"
        searchable
      />
      {connections !== null && connections.length === 0 && (
        <Text size="sm" c="dimmed">
          No Blob connections you can use. Add one under Integrations.
        </Text>
      )}
      {connectionId && (
        <>
          <SegmentedControl
            value={mode}
            onChange={(v) => {
              setMode(v as "files" | "folder");
              reset();
            }}
            data={[
              { value: "files", label: "Pick files" },
              { value: "folder", label: "Whole folder" },
            ]}
            style={{ alignSelf: "flex-start" }}
          />
          {mode === "files" ? (
            <BlobFilePicker
              key={`files-${connectionId}`}
              connectionId={connectionId}
              label="Files"
              value={null}
              onChange={() => undefined}
              values={files}
              onToggle={toggle}
              acceptedExtensions={UPLOAD_EXTENSIONS}
            />
          ) : (
            <BlobFilePicker
              key={`folder-${connectionId}`}
              connectionId={connectionId}
              label="Folder — everything under it is added, including subfolders"
              value={folder}
              onChange={setFolder}
              mode="folder"
            />
          )}
          <Group justify="flex-end">
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => void submit()}
              loading={busy}
              disabled={!canSubmit}
            >
              {mode === "folder"
                ? "Add folder"
                : `Add ${files.length} file${files.length === 1 ? "" : "s"}`}
            </Button>
          </Group>
        </>
      )}
    </Stack>
  );
}
